import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { Heart, Compass, MessageCircle, User, LogOut, BarChart3, Crown, Eye, Bell, Activity as ActivityIcon, BookOpen, Receipt, Newspaper, Sparkles, Menu, X } from "lucide-react";
import { toast } from "sonner";
import CoachingLink from "./CoachingLink";

const navItem = "px-3 py-2 rounded-full text-sm tracking-wide transition-colors flex items-center gap-2";

// Single source of truth for primary navigation entries.
// Faturação intentionally omitted — it now lives inside /profile.
// Painel was merged into Actividade — KPIs render at the top of /actividade.
const NAV_LINKS = [
  { to: "/discover",    label: "Descobrir",    icon: Compass,      testid: "nav-discover" },
  { to: "/actividade",  label: "Actividade",   icon: ActivityIcon, testid: "nav-activity" },
  { to: "/encontros",   label: "Diário",       icon: BookOpen,     testid: "nav-encounters" },
  { to: "/visitantes",  label: "Visitantes",   icon: Eye,          testid: "nav-views" },
  { to: "/artigos",     label: "Artigos",      icon: Newspaper,    testid: "nav-articles" },
  { to: "/profile",     label: "Perfil",       icon: User,         testid: "nav-profile" },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [tokens, setTokens] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastTokenCount = useRef(null);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetchUnread = async () => {
      try {
        const res = await client.get("/notifications");
        if (active) setUnread(res.data.unread || 0);
      } catch (err) {
        // 401 fires naturally during logout; log the rest for debugging.
        if (err?.response?.status !== 401) {
          console.warn("[Navbar] unread poll failed", err);
        }
      }
    };
    const fetchTokens = async () => {
      try {
        const res = await client.get("/discovery-tokens");
        if (!active) return;
        const next = res.data;
        if (lastTokenCount.current !== null
            && typeof next.available === "number"
            && next.available > lastTokenCount.current) {
          const delta = next.available - lastTokenCount.current;
          toast.success(
            `+${delta} ficha${delta === 1 ? "" : "s"} de descoberta · Ganhaste um bónus.`,
            { duration: 6000, icon: "🎟️" }
          );
        }
        lastTokenCount.current = next.available ?? 0;
        setTokens(next);
      } catch (err) {
        if (err?.response?.status !== 401) {
          console.warn("[Navbar] tokens poll failed", err);
        }
      }
    };
    fetchUnread();
    fetchTokens();
    const t = setInterval(() => { fetchUnread(); fetchTokens(); }, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock background scroll while drawer is open
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  if (!user) return null;
  const initial = (user.name || "?").charAt(0).toUpperCase();

  const handleLogout = () => { logout(); navigate("/"); };

  // Premium-only entry that we want exposed in both desktop & mobile menus
  const adminPremiumLinks = user.is_premium
    ? [{ to: "/admiradores", label: "Admiradores", icon: Eye, testid: "nav-admirers" }]
    : [];

  // Inserted between /artigos and /profile for visual grouping
  const desktopLinks = [
    ...NAV_LINKS.slice(0, 5),     // Descobrir → Artigos
    ...adminPremiumLinks,          // Admiradores (premium only)
    ...NAV_LINKS.slice(5),         // Perfil
  ];

  const billingLink = { to: "/faturacao", label: "Faturação", icon: Receipt, testid: "nav-billing" };
  const mobileLinks = [...desktopLinks, billingLink];

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-40 backdrop-blur-2xl bg-[#0C0E12]/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-16 flex items-center justify-between gap-3">
        {/* Mobile: hamburger */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="md:hidden w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-[#DCD7D2]"
          data-testid="nav-mobile-toggle"
          aria-label="Abrir menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <Link to="/discover" className="flex items-center gap-2" data-testid="nav-logo">
          <Heart className="w-5 h-5 text-[#C5A880]" />
          <span className="font-display text-lg sm:text-xl tracking-tight">Soulmatch</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
          {desktopLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testid}
              className={({isActive}) => `${navItem} ${isActive ? "text-[#C5A880] bg-white/5" : "text-[#DCD7D2] hover:bg-white/5"}`}>
              <l.icon className="w-4 h-4" /> {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <CoachingLink />
          <Link to="/notificacoes" data-testid="nav-bell"
            className="relative w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-[#DCD7D2] hover:text-[#C5A880] transition-colors">
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C5A880] text-[#0C0E12] text-[10px] font-bold flex items-center justify-center" data-testid="nav-bell-count">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Link>
          {!user.is_premium && tokens && tokens.available > 0 && (
            <Link to="/premium#fichas" data-testid="nav-tokens"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#DCD7D2] hover:border-[#C5A880]/40 hover:text-[#C5A880] text-xs transition-colors"
              title={`Tens ${tokens.available} ficha(s) de descoberta. Cada uma desbloqueia um perfil para sempre.`}>
              <Sparkles className="w-3.5 h-3.5 text-[#C5A880]" /> {tokens.available} ficha{tokens.available === 1 ? "" : "s"}
            </Link>
          )}
          {!user.is_premium ? (
            <NavLink to="/premium" data-testid="nav-premium-cta"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/40 text-[#C5A880] hover:bg-[#C5A880]/20 text-sm transition-colors">
              <Crown className="w-3.5 h-3.5" /> Premium
            </NavLink>
          ) : (
            <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C5A880] text-[#0C0E12] text-xs font-bold uppercase tracking-wider" data-testid="nav-premium-badge">
              <Crown className="w-3 h-3" /> Premium
            </span>
          )}
          <div className="hidden sm:flex w-9 h-9 rounded-full bg-[#C5A880] text-[#0C0E12] items-center justify-center font-semibold">
            {initial}
          </div>
          <button onClick={handleLogout} data-testid="nav-logout"
            className="flex items-center gap-2 text-sm text-[#A29F9B] hover:text-[#C5A880] transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>

      {/* Mobile drawer — portalled to <body> so the header's backdrop-filter
          does NOT trap fixed-positioned descendants inside the 64px header box. */}
      {mobileOpen && createPortal(
        <div className="md:hidden" data-testid="nav-mobile-drawer">
          <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="fixed top-0 bottom-0 left-0 z-[60] w-[82%] max-w-sm bg-[#0C0E12] border-r border-white/10 flex flex-col">
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/5">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#C5A880]" />
                <span className="font-display text-xl">Soulmatch</span>
              </div>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center text-[#DCD7D2]"
                data-testid="nav-mobile-close"
                aria-label="Fechar menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#C5A880] text-[#0C0E12] flex items-center justify-center font-semibold">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="text-sm text-[#F5F2EE] truncate">{user.name || "Soulmatch"}</div>
                <div className="text-xs text-[#A29F9B] truncate">{user.email}</div>
              </div>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-3">
              {mobileLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  className={({isActive}) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] mb-1 ${
                      isActive ? "text-[#C5A880] bg-white/5" : "text-[#DCD7D2] hover:bg-white/5"
                    }`}
                >
                  <l.icon className="w-4 h-4" /> {l.label}
                </NavLink>
              ))}
              {!user.is_premium && tokens && tokens.available > 0 && (
                <NavLink to="/premium#fichas" data-testid="mobile-nav-tokens"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] mb-1 text-[#DCD7D2] hover:bg-white/5">
                  <Sparkles className="w-4 h-4 text-[#C5A880]" /> {tokens.available} ficha{tokens.available === 1 ? "" : "s"} de descoberta
                </NavLink>
              )}
              {!user.is_premium && (
                <NavLink to="/premium" data-testid="mobile-nav-premium"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] mb-1 bg-[#C5A880]/10 border border-[#C5A880]/30 text-[#C5A880]">
                  <Crown className="w-4 h-4" /> Subscrever Premium
                </NavLink>
              )}
            </nav>
            <button onClick={handleLogout}
              data-testid="mobile-nav-logout"
              className="m-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/10 text-[#DCD7D2] hover:bg-white/5">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </aside>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
